/**
 * Manual override dropdown options keyed by the atom's current screening verdict.
 */

import {
  MANUAL_OVERRIDE_DISPOSITION_LABELS,
  ManualOverrideDisposition,
} from "./manualOverrideDisposition";

/** Normalized criteria verdict buckets for override option lists. */
export type CriteriaVerdictBucket = "met" | "not_met" | "action_pending";

/** One selectable override action in the modal. */
export interface ManualOverrideOption {
  value: ManualOverrideDisposition;
  label: string;
}

const MET_OPTIONS: ManualOverrideDisposition[] = [
  ManualOverrideDisposition.MarkNotMetContinue,
  ManualOverrideDisposition.MarkNotMetIneligible,
  ManualOverrideDisposition.SkipContinue,
];

const NOT_MET_OPTIONS: ManualOverrideDisposition[] = [
  ManualOverrideDisposition.MarkMetContinue,
  ManualOverrideDisposition.SkipContinue,
];

const ACTION_PENDING_OPTIONS: ManualOverrideDisposition[] = [
  ManualOverrideDisposition.MarkMetContinue,
  ManualOverrideDisposition.MarkNotMetContinue,
  ManualOverrideDisposition.MarkNotMetIneligible,
  ManualOverrideDisposition.SkipContinue,
];

/**
 * Classify a raw atom `status` from screening into Met / Not Met / Action Pending.
 *
 * @param atomStatus - Agent verdict on the atom (e.g. PASS, FAIL, DATA_MISSING)
 * @returns Bucket used to choose override actions
 */
export function normalizeCriteriaVerdictBucket(
  atomStatus: string,
): CriteriaVerdictBucket {
  const normalized = atomStatus.trim().toUpperCase();
  if (normalized === "PASS") {
    return "met";
  }
  if (normalized === "FAIL") {
    return "not_met";
  }
  return "action_pending";
}

/**
 * Build the override dropdown for the atom's current screening verdict.
 *
 * @param atomStatus - Agent verdict on the atom
 * @returns Select options in product-defined order
 */
export function getManualOverrideOptions(
  atomStatus: string,
): ManualOverrideOption[] {
  const bucket = normalizeCriteriaVerdictBucket(atomStatus);
  const dispositionList =
    bucket === "met"
      ? MET_OPTIONS
      : bucket === "not_met"
        ? NOT_MET_OPTIONS
        : ACTION_PENDING_OPTIONS;

  return dispositionList.map((value) => ({
    value,
    label: MANUAL_OVERRIDE_DISPOSITION_LABELS[value],
  }));
}
